import {
  CardActionArea,
  Chip,
  Divider,
  Card as MUICard,
  Tooltip,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  CheckCircleOutlineRounded,
  ErrorOutlineRounded,
  FolderOutlined,
  KeyboardArrowRightRounded
} from '@material-ui/icons';
import { Alert, Color } from '@material-ui/lab';
import clsx from 'clsx';
import moment from 'moment';
import { Link as RRLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import BotIcon from '../icon/BotIcon';
import { WorkflowStatusLabel } from '../pages/ProjectDocumentRevision/DocumentRevisionView';
import { ApprovalTypeLabel } from '../pages/ProjectDocumentWorkflow/ProjectDocumentWorkflowApproval';
import { MyTaskHistory } from '../pages/UserApproval/userApprovalSlice';

interface DocMyTaskHistoryCardProps {
  renderMyHistory: MyTaskHistory[];
}

export default function DocMyTaskHistoryCard({
  renderMyHistory
}: DocMyTaskHistoryCardProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const today = moment();

  return (
    <div>
      {renderMyHistory.map((userApproval, index) => {
        const { project, discipline, ...projectDocument } =
          userApproval.projectDocumentWorkflowStep.projectDocumentWorkflow
            .projectDocumentRevision.projectDocument;
        const { purpose, revision } =
          userApproval.projectDocumentWorkflowStep.projectDocumentWorkflow
            .projectDocumentRevision;
        const { projectDocumentWorkflow } =
          userApproval.projectDocumentWorkflowStep;

        const to = `/approvals/${userApproval.projectDocumentWorkflowStep.projectDocumentWorkflowId}?ua=${userApproval.id}`;
        return (
          <MUICard className={classes.card} key={index}>
            <CardActionArea
              className={clsx(classes.container, classes.flexProjectName)}
              component={RRLink}
              to={`/projects/${project.id}`}
            >
              <FolderOutlined
                className={classes.projectIcon}
                fontSize="small"
                color="primary"
              />
              <Typography variant="caption">
                {`${project.projectNo} : ${project.name}`}
              </Typography>
              <KeyboardArrowRightRounded color="action" />
            </CardActionArea>
            <Divider />
            <CardActionArea
              className={clsx(classes.container, classes.paddingAll)}
              component={RRLink}
              to={to}
            >
              <div className={classes.flexMyTask}>
                <div className={classes.divWidth50}>
                  <Typography variant="body1" className={classes.typoDocName}>
                    {discipline && (
                      <Tooltip title={discipline?.cfihosName ?? ''} arrow>
                        <Chip
                          className={classes.disciplineChip}
                          component="span"
                          label={discipline.name}
                          variant="outlined"
                          size="small"
                        />
                      </Tooltip>
                    )}
                    {projectDocument.name}
                  </Typography>
                  <Typography variant="subtitle2">
                    {projectDocument.no}
                  </Typography>
                  <Typography variant="caption">
                    {t('docMyTaskHistoryCard.purpose')}: {purpose.abbreviation}
                    {', '}
                    {t('docMyTaskHistoryCard.revision')}: {revision.name}
                  </Typography>
                </div>
                <div
                  className={clsx(
                    classes.divWidth50,
                    classes.textItemRightOnMobile
                  )}
                >
                  <Typography variant="caption" display="block">
                    {t('docMyTaskHistoryCard.workflow')}: {projectDocumentWorkflow.name}
                  </Typography>
                  <div className={classes.divChipWorkflowStateAndDueDate}>
                    <Chip
                      className={clsx(
                        classes.chip,
                        {
                          [classes.chipDelay]:
                            projectDocumentWorkflow.workflowStatusId ===
                              WorkflowStatusLabel.InProgress &&
                            moment(
                              projectDocumentWorkflow.currentWorkflowStep
                                .dueDate
                            ) < today
                        },
                        {
                          [classes.chipOnDue]:
                            projectDocumentWorkflow.workflowStatusId ===
                              WorkflowStatusLabel.InProgress &&
                            moment(
                              projectDocumentWorkflow.currentWorkflowStep
                                .dueDate
                            ) >= today
                        },
                        {
                          [classes.chipErrorOutlineColor]:
                            projectDocumentWorkflow.workflowStatusId ===
                            WorkflowStatusLabel.Rejected
                        },
                        {
                          [classes.chipSuccessOutlineColor]:
                            projectDocumentWorkflow.workflowStatusId ===
                            WorkflowStatusLabel.Completed
                        }
                      )}
                      variant="outlined"
                      label={
                        projectDocumentWorkflow.workflowStatusId ===
                        WorkflowStatusLabel.InProgress
                          ? t('docMyTaskHistoryCard.waitFor', { stepName: projectDocumentWorkflow.currentWorkflowStep.workflowStep.name })
                          : projectDocumentWorkflow.workflowStatus.name
                      }
                      icon={
                        projectDocumentWorkflow.workflowStatusId ===
                        WorkflowStatusLabel.Completed ? (
                          <CheckCircleOutlineRounded
                            className={classes.chipSuccessOutlineColor}
                          />
                        ) : projectDocumentWorkflow.workflowStatusId ===
                          WorkflowStatusLabel.Rejected ? (
                          <ErrorOutlineRounded
                            className={classes.chipErrorOutlineColor}
                          />
                        ) : undefined
                      }
                      size="small"
                    />
                    <Typography variant="subtitle2" color="textSecondary">
                      {projectDocumentWorkflow.workflowStatusId ===
                      WorkflowStatusLabel.InProgress
                        ? t('docMyTaskHistoryCard.dueDate', { date: moment(projectDocumentWorkflow.currentWorkflowStep.dueDate).format('ll') })
                        : t('docMyTaskHistoryCard.at', { date: moment(projectDocumentWorkflow.finishedDate).format('lll') })}
                    </Typography>
                  </div>
                </div>
              </div>
              <Alert
                className={classes.alert}
                color={getAnswerDetails(userApproval, t).color}
                icon={getAnswerDetails(userApproval, t).icon}
              >
                <b>{getAnswerDetails(userApproval, t).text}</b> {t('docMyTaskHistoryCard.at', { date: moment(userApproval.submittedDate).format('lll') })}
              </Alert>
            </CardActionArea>
          </MUICard>
        );
      })}
    </div>
  );
}

function getAnswerDetails(userApproval: MyTaskHistory, t: TFunction) {
  let color: Color = 'info';
  let icon: JSX.Element | boolean = false;
  let text = '';

  if (userApproval.isAutoApproval) {
    color = 'info';
    icon = <BotIcon fontSize="small" />;
    text = t('docMyTaskHistoryCard.answerDetails.autoApproved');
  } else if (userApproval.approvalTypeId === ApprovalTypeLabel.Approve) {
    color = 'success';
    icon = false;
    text = t('docMyTaskHistoryCard.answerDetails.youApproved');
  } else if (userApproval.approvalTypeId === ApprovalTypeLabel.Reject) {
    color = 'error';
    icon = false;
    text = t('docMyTaskHistoryCard.answerDetails.youRejected');
  } else if (userApproval.approvalTypeId === ApprovalTypeLabel.Acknowledge) {
    color = 'info';
    icon = false;
    text = t('docMyTaskHistoryCard.answerDetails.youAcknowledged');
  }

  if (userApproval.approvalCode) {
    let approvalCodeName = userApproval.approvalCode.name;

    const colonIndex = approvalCodeName.indexOf(':');

    if (colonIndex > -1) {
      approvalCodeName = approvalCodeName
        .substring(0, colonIndex)
        .replace(/\s+/g, '');
    }

    text = `${approvalCodeName} • ${text}`;
  }

  return { color, icon, text };
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      margin: theme.spacing(0, 0, 1.5, 0),
      '&:last-child': {
        marginBottom: 0
      },
      width: '100%',
      border: '2px solid',
      borderColor: theme.palette.background.paper,
      borderRadius: 16,
      transition: '0.4s',
      '&:hover, &.Mui-focusVisible': {
        borderColor: theme.palette.primary.main
      }
    },
    container: {
      textDecoration: 'none',
      color: theme.palette.text.primary
    },
    flexProjectName: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      padding: theme.spacing(0, 0.5, 0, 1)
    },
    flexMyTask: {
      display: 'flex',
      alignItems: 'flex-end',
      [theme.breakpoints.down('sm')]: { display: 'block' },
      marginBottom: theme.spacing(1)
    },
    chip: {
      marginRight: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        marginRight: 0
      },
      cursor: 'pointer'
    },
    chipOnDue: {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText
    },
    chipDelay: {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    chipSuccessOutlineColor: {
      borderColor: theme.palette.success.main,
      color: theme.palette.success.main
    },
    chipErrorOutlineColor: {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main
    },
    disciplineChip: {
      margin: theme.spacing(0, 1, 0, -0.5),
      cursor: 'pointer',
      fontWeight: 'normal',
      fontSize: 13
    },
    typoDocName: {
      wordBreak: 'break-word',
      lineHeight: '100%'
    },
    divWidth50: {
      width: '50%',
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      }
    },
    divChipWorkflowStateAndDueDate: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    textItemRightOnMobile: {
      paddingLeft: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        paddingLeft: theme.spacing(0),
        paddingTop: theme.spacing(0.5),
        textAlign: 'right'
      }
    },
    paddingAll: {
      padding: theme.spacing(1, 1.5)
    },
    projectIcon: {
      marginRight: theme.spacing(0.5)
    },
    alert: {
      padding: theme.spacing(0, 1.5)
    }
  })
);
